import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Spinner from "../Components/Spinner";
import { BASE_URL_API } from "../Constants";
import { useAuth } from "../hooks/AuthProvider";
import { LanguageContext } from "../context/LanguageContext";
import { AI_REPORT_PARAM_INPUT_TYPES, AI_REPORT_PDF_MODES, normalizeAiReportParamInputType, normalizeAiReportParamOptions, normalizeAiReportPdfMode, setAiReportTypesCache } from "../utils/aiReportTypes";
import systemConfigTranslations from "../languages/systemConfigurations.json";
import "./SystemConfigurations.css";

const createBlankReportType = () => ({ value: "", label: "", apiUrl: "", pdfMode: "ai_pdf", params: [] });
const createBlankParam = () => ({ key: "", label: "", required: true, placeholder: "", inputType: "text", options: [] });
const normalizeAiReportAvailability = (value = {}) => ({
  devices: value.devices !== false,
  dashboards: value.dashboards !== false,
  templates: value.templates !== false,
});

const normalizeReportTypes = (value) =>
  (Array.isArray(value?.types) ? value.types : []).map((type) => ({
    value: String(type?.value || ""),
    label: String(type?.label || ""),
    apiUrl: String(type?.apiUrl || ""),
    pdfMode: normalizeAiReportPdfMode(type?.pdfMode || type?.outputMode || type?.renderer, type?.apiUrl),
    params: (Array.isArray(type?.params) ? type.params : []).map((param) => ({
      key: String(param?.key || ""),
      label: String(param?.label || ""),
      required: param?.required !== false,
      placeholder: String(param?.placeholder || ""),
      inputType: normalizeAiReportParamInputType(param?.inputType ?? param?.type),
      options: normalizeAiReportParamOptions(param?.options ?? param?.values),
    })),
  }));

const serializeOptions = (options = []) =>
  normalizeAiReportParamOptions(options)
    .map((option) => option.value)
    .join(", ");

const normalizeMode = (mode) =>
  mode === "anovator_redirect" ? "anovator_redirect" : "internal_pdf";

const modeIcons = {
  internal_pdf: <PictureAsPdfIcon />,
  anovator_redirect: <OpenInNewIcon />,
};

const SystemConfigurations = () => {
  const { user } = useAuth();
  const { lang } = useContext(LanguageContext);
  const t = systemConfigTranslations[lang] || systemConfigTranslations.en;
  const isRtl = ["ar", "ur"].includes(lang);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("internal_pdf");
  const [savedMode, setSavedMode] = useState("internal_pdf");
  const [reportTypes, setReportTypes] = useState([]);
  const [savedReportTypes, setSavedReportTypes] = useState([]);
  const [aiReportAvailability, setAiReportAvailability] = useState(normalizeAiReportAvailability());
  const [savedAiReportAvailability, setSavedAiReportAvailability] = useState(normalizeAiReportAvailability());
  const [selectedTypeIndex, setSelectedTypeIndex] = useState(0);
  const [message, setMessage] = useState("");

  const isSuper = String(user?.role || "").toLowerCase() === "super";
  const isDirty =
    mode !== savedMode ||
    JSON.stringify(reportTypes) !== JSON.stringify(savedReportTypes) ||
    JSON.stringify(aiReportAvailability) !== JSON.stringify(savedAiReportAvailability);

  const downloadModes = useMemo(
    () => ({
      internal_pdf: { ...t.healthModes.internal_pdf, icon: modeIcons.internal_pdf },
      anovator_redirect: { ...t.healthModes.anovator_redirect, icon: modeIcons.anovator_redirect },
    }),
    [t.healthModes]
  );

  useEffect(() => {
    const fetchConfig = async () => {
      if (!isSuper) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${BASE_URL_API}/configuration/system`);
        const nextMode = normalizeMode(res.data?.data?.healthReport?.value?.downloadMode);
        const nextReportTypes = normalizeReportTypes(res.data?.data?.aiReportTypes?.value);
        const nextAvailability = normalizeAiReportAvailability(res.data?.data?.aiReportAvailability?.value);
        setMode(nextMode);
        setSavedMode(nextMode);
        setReportTypes(nextReportTypes);
        setSavedReportTypes(nextReportTypes);
        setAiReportAvailability(nextAvailability);
        setSavedAiReportAvailability(nextAvailability);
        setSelectedTypeIndex(0);
      } catch (err) {
        console.error(err);
        setMessage(t.loadError);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [isSuper, t.loadError]);

  const currentModeLabel = downloadModes[savedMode]?.title || t.healthModes.internal_pdf.title;
  const selectedType = reportTypes[selectedTypeIndex] || null;
  const updateAiReportAvailability = (key, checked) => {
    setAiReportAvailability((prev) => ({ ...prev, [key]: checked }));
  };

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const res = await axios.put(`${BASE_URL_API}/configuration/system`, {
        healthReport: { downloadMode: mode },
        aiReportTypes: { types: reportTypes },
        aiReportAvailability,
      });
      const nextMode = normalizeMode(res.data?.data?.healthReport?.value?.downloadMode);
      const nextReportTypes = normalizeReportTypes(res.data?.data?.aiReportTypes?.value);
      const nextAvailability = normalizeAiReportAvailability(res.data?.data?.aiReportAvailability?.value);
      setMode(nextMode);
      setSavedMode(nextMode);
      setReportTypes(nextReportTypes);
      setSavedReportTypes(nextReportTypes);
      setAiReportAvailability(nextAvailability);
      setSavedAiReportAvailability(nextAvailability);
      setAiReportTypesCache(nextReportTypes, nextAvailability);
      setSelectedTypeIndex(Math.min(selectedTypeIndex, Math.max(0, nextReportTypes.length - 1)));
      setMessage(t.saveSuccess);
    } catch (err) {
      console.error(err);
      setMessage(t.saveError);
    } finally {
      setSaving(false);
    }
  };

  const updateType = (index, patch) => {
    setReportTypes((prev) => prev.map((type, idx) => (idx === index ? { ...type, ...patch } : type)));
  };

  const addType = () => {
    setReportTypes((prev) => {
      const next = [...prev, createBlankReportType()];
      setSelectedTypeIndex(next.length - 1);
      return next;
    });
  };

  const deleteType = (index) => {
    setReportTypes((prev) => prev.filter((_, idx) => idx !== index));
    setSelectedTypeIndex((prev) => Math.max(0, prev - 1));
  };

  const updateParam = (typeIndex, paramIndex, patch) => {
    setReportTypes((prev) =>
      prev.map((type, idx) =>
        idx === typeIndex
          ? {
              ...type,
              params: type.params.map((param, pIdx) =>
                pIdx === paramIndex ? { ...param, ...patch } : param
              ),
            }
          : type
      )
    );
  };

  const addParam = (typeIndex) => {
    setReportTypes((prev) =>
      prev.map((type, idx) =>
        idx === typeIndex ? { ...type, params: [...type.params, createBlankParam()] } : type
      )
    );
  };

  const deleteParam = (typeIndex, paramIndex) => {
    setReportTypes((prev) =>
      prev.map((type, idx) =>
        idx === typeIndex
          ? { ...type, params: type.params.filter((_, pIdx) => pIdx !== paramIndex) }
          : type
      )
    );
  };

  if (loading) return <Spinner />;

  if (!isSuper) {
    return (
      <div className="configuration system-config-page" dir={isRtl ? "rtl" : "ltr"}>
        <div className="system-config-shell">
          <div className="system-config-empty">{t.noPermission}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="configuration system-config-page" dir={isRtl ? "rtl" : "ltr"}>
      <div className="system-config-shell">
        <header className="system-config-header">
          <div className="system-config-title-mark">
            <SettingsSuggestIcon />
          </div>
          <div>
            <p className="system-config-eyebrow">{t.superControls}</p>
            <h2>{t.title}</h2>
            <p>{t.description}</p>
          </div>
        </header>

        <section className="system-config-status">
          <div>
            <span>{t.activeHealthReport}</span>
            <strong>{currentModeLabel}</strong>
          </div>
          <CheckCircleIcon />
        </section>

        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>{t.healthTitle}</h3>
              <p>{t.healthDescription}</p>
            </div>
          </div>

          <div className="system-config-options" role="radiogroup" aria-label={t.healthTitle}>
            {Object.entries(downloadModes).map(([key, option]) => {
              const selected = mode === key;
              return (
                <button
                  type="button"
                  key={key}
                  className={`system-config-option ${selected ? "selected" : ""}`}
                  onClick={() => setMode(key)}
                  role="radio"
                  aria-checked={selected}
                >
                  <span className="system-config-option-icon">{option.icon}</span>
                  <span>
                    <strong>{option.title}</strong>
                    <small>{option.description}</small>
                  </span>
                  <span className="system-config-radio" />
                </button>
              );
            })}
          </div>
        </section>

        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>{t.aiAvailabilityTitle}</h3>
              <p>{t.aiAvailabilityDescription}</p>
            </div>
          </div>

          <div className="system-config-switch-grid">
            {["devices", "dashboards", "templates"].map((scope) => (
              <label className="system-config-switch-card" key={scope}>
                <span>
                  <strong>{t.aiAvailabilityScopes?.[scope] || scope}</strong>
                  <small>{t.aiAvailabilityScopeDescriptions?.[scope] || ""}</small>
                </span>
                <input
                  type="checkbox"
                  checked={aiReportAvailability[scope] !== false}
                  onChange={(event) => updateAiReportAvailability(scope, event.target.checked)}
                />
              </label>
            ))}
          </div>
        </section>

        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>{t.aiTypesTitle}</h3>
              <p>{t.aiTypesDescription}</p>
            </div>
            <button type="button" className="system-config-add-btn" onClick={addType}>
              <AddIcon /> {t.addType}
            </button>
          </div>

          <div className="system-config-report-layout">
            <div className="system-config-type-list">
              {reportTypes.length === 0 ? (
                <div className="system-config-type-empty">{t.noTypes}</div>
              ) : reportTypes.map((type, index) => (
                <button
                  type="button"
                  key={`${type.value}-${index}`}
                  className={`system-config-type-row ${selectedTypeIndex === index ? "selected" : ""}`}
                  onClick={() => setSelectedTypeIndex(index)}
                >
                  <strong>{type.label || t.untitledType}</strong>
                  <small>{type.value || t.missingKey}</small>
                </button>
              ))}
            </div>

            {selectedType ? (
              <div className="system-config-type-editor">
                <div className="system-config-editor-grid">
                  <label>
                    <span>{t.typeKey}</span>
                    <input value={selectedType.value} onChange={(e) => updateType(selectedTypeIndex, { value: e.target.value })} placeholder="power-meter" />
                  </label>
                  <label>
                    <span>{t.displayName}</span>
                    <input value={selectedType.label} onChange={(e) => updateType(selectedTypeIndex, { label: e.target.value })} placeholder="Power Meter" />
                  </label>
                  <label className="system-config-wide">
                    <span>{t.reportApi}</span>
                    <input value={selectedType.apiUrl} onChange={(e) => updateType(selectedTypeIndex, { apiUrl: e.target.value })} placeholder="/pyv1/reports/power-meter/upload-preview" />
                  </label>
                  <label>
                    <span>{t.pdfMode}</span>
                    <select value={selectedType.pdfMode || "ai_pdf"} onChange={(e) => updateType(selectedTypeIndex, { pdfMode: e.target.value })}>
                      {AI_REPORT_PDF_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>{t.pdfModes?.[mode.value] || mode.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="system-config-param-head">
                  <h4>{t.parameters}</h4>
                  <button type="button" className="system-config-add-btn secondary" onClick={() => addParam(selectedTypeIndex)}>
                    <AddIcon /> {t.addParam}
                  </button>
                </div>

                <div className="system-config-param-list">
                  {selectedType.params.map((param, paramIndex) => (
                    <div className="system-config-param-row" key={`${param.key}-${paramIndex}`}>
                      <input value={param.key} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { key: e.target.value })} placeholder={t.paramKey} />
                      <input value={param.label} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { label: e.target.value })} placeholder={t.paramLabel} />
                      <select value={param.inputType || "text"} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { inputType: e.target.value })}>
                        {AI_REPORT_PARAM_INPUT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>{t.paramTypes?.[type.value] || type.label}</option>
                        ))}
                      </select>
                      <input value={param.placeholder} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { placeholder: e.target.value })} placeholder={t.paramPlaceholder} />
                      <input value={serializeOptions(param.options)} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { options: normalizeAiReportParamOptions(e.target.value) })} placeholder={t.paramOptions} />
                      <label className="system-config-check">
                        <input type="checkbox" checked={param.required !== false} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { required: e.target.checked })} />
                        {t.required}
                      </label>
                      <button type="button" className="system-config-icon-btn" onClick={() => deleteParam(selectedTypeIndex, paramIndex)} aria-label={t.deleteParam}>
                        <DeleteOutlineIcon />
                      </button>
                    </div>
                  ))}
                  {selectedType.params.length === 0 ? <div className="system-config-type-empty">{t.noParams}</div> : null}
                </div>

                <div className="system-config-danger-row">
                  <button type="button" className="system-config-delete-btn" onClick={() => deleteType(selectedTypeIndex)}>
                    <DeleteOutlineIcon /> {t.deleteType}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <div className="system-config-actions">
          {message ? <span className="system-config-message">{message}</span> : <span />}
          <button type="button" className="devices-primary-btn" disabled={!isDirty || saving} onClick={save}>
            {saving ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemConfigurations;
